import { Avatar, Button, Popconfirm, Spin, Table, Tag, Typography } from 'antd';
import { DesktopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UserSession } from '@core/entities/UserSession';
import {
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
} from '@presentation/hooks/useSessions';
import { useAppMessage } from '@presentation/hooks/useAppMessage';

export const SessionsTab = () => {
  const { data: sessions, isLoading } = useSessions();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const message = useAppMessage();

  if (isLoading) {
    return <Spin />;
  }

  const columns: ColumnsType<UserSession> = [
    {
      title: 'Device',
      key: 'device',
      render: (_, session) => (
        <div className="flex items-center gap-3">
          <Avatar icon={<DesktopOutlined />} />
          <div>
            <div>
              {session.deviceName ?? 'Unknown device'}
              {session.isCurrent && (
                <Tag color="blue" className="ml-2">
                  This device
                </Tag>
              )}
            </div>
            <Typography.Text type="secondary" className="text-xs">
              {[session.browser, session.platform, session.ipAddress].filter(Boolean).join(' · ')}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Last active',
      key: 'lastActive',
      width: 200,
      render: (_, session) => new Date(session.lastActivityAt).toLocaleString(),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, session) =>
        !session.isCurrent && (
          <Popconfirm
            title="Revoke this session?"
            onConfirm={() =>
              revoke.mutate(session.id, { onSuccess: () => message.success('Session revoked.') })
            }
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger size="small" loading={revoke.isPending}>
              Revoke
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <Typography.Title level={5} className="!mb-0">
          Active sessions
        </Typography.Title>
        <Popconfirm
          title="Sign out other sessions?"
          description="This will immediately revoke every session except this one."
          onConfirm={() =>
            revokeOthers.mutate(undefined, {
              onSuccess: () => message.success('Other sessions signed out.'),
            })
          }
          okText="Sign out others"
          okButtonProps={{ danger: true }}
        >
          <Button loading={revokeOthers.isPending}>Sign out other sessions</Button>
        </Popconfirm>
      </div>

      <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={sessions} />
    </div>
  );
};
